import { useMemo } from 'react';
import { getPropertyValue } from './matrixClassification';

const DEFAULT_RANGE = { min: 0, max: 100, hasData: false, coverage: 0 };

/**
 * Compute the actual min/max range for a given property across all hexagons,
 * along with data availability info.
 *
 * Currently scans all hexagons client-side. When the backend implements
 * Requirement 6, the available_properties response could include per-property
 * min/max ranges, which would eliminate the need for client-side scanning.
 *
 * @param {Array} hexagons - Array of OpenLayers hexagon features
 * @param {string|null} propertyId - Property ID to scan (e.g. 'mean_height', 'count')
 * @returns {{ min: number, max: number, hasData: boolean, coverage: number }}
 */
export default function usePropertyRanges(hexagons, propertyId) {
	return useMemo(() => {
		if (!hexagons?.length || !propertyId) return DEFAULT_RANGE;

		let min = Infinity;
		let max = -Infinity;
		let validCount = 0;

		for (const h of hexagons) {
			const meta = h.values_?.data?.metadata;
			if (!meta) continue;
			const v = getPropertyValue(meta, propertyId);
			if (v === null || isNaN(v)) continue;
			validCount++;
			if (v < min) min = v;
			if (v > max) max = v;
		}

		if (min === Infinity) return DEFAULT_RANGE;

		return {
			min: Math.floor(min),
			max: Math.ceil(max),
			hasData: true,
			coverage: Math.round((validCount / hexagons.length) * 100),
		};
	}, [hexagons, propertyId]);
}
